/** Read GPS and capture time from JPEG EXIF when the camera/gallery file has it. */
export type JpegGps = {
  lat: number;
  lng: number;
};

export type JpegPhotoMeta = {
  gps: JpegGps | null;
  capturedAt: Date | null;
};

export const readJpegGps = async (file: File): Promise<JpegGps | null> => {
  const meta = await readJpegPhotoMeta(file);
  return meta.gps;
};

export const readJpegPhotoMeta = async (file: File): Promise<JpegPhotoMeta> => {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return { gps: null, capturedAt: null };
  }
  let offset = 2;
  let gps: JpegGps | null = null;
  let capturedAt: Date | null = null;
  while (offset + 4 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      break;
    }
    const marker = bytes[offset + 1];
    const size = (bytes[offset + 2] << 8) | bytes[offset + 3];
    if (marker === 0xe1 && size >= 8) {
      const start = offset + 4;
      const parsed = parseExif(bytes, start, start + size - 2);
      if (parsed.gps && !gps) {
        gps = parsed.gps;
      }
      if (parsed.capturedAt && !capturedAt) {
        capturedAt = parsed.capturedAt;
      }
    }
    if (marker === 0xda) {
      break;
    }
    offset += 2 + size;
  }
  return { gps, capturedAt };
};

const parseExif = (bytes: Uint8Array, start: number, end: number): JpegPhotoMeta => {
  if (end - start < 14) {
    return { gps: null, capturedAt: null };
  }
  const header = String.fromCharCode(bytes[start], bytes[start + 1], bytes[start + 2], bytes[start + 3]);
  if (header !== "Exif") {
    return { gps: null, capturedAt: null };
  }
  const tiff = start + 6;
  const le = bytes[tiff] === 0x49 && bytes[tiff + 1] === 0x49;
  const be = bytes[tiff] === 0x4d && bytes[tiff + 1] === 0x4d;
  if (!le && !be) {
    return { gps: null, capturedAt: null };
  }
  const u16 = (at: number) =>
    le ? bytes[at] | (bytes[at + 1] << 8) : (bytes[at] << 8) | bytes[at + 1];
  const u32 = (at: number) =>
    le
      ? (bytes[at] | (bytes[at + 1] << 8) | (bytes[at + 2] << 16) | (bytes[at + 3] << 24)) >>> 0
      : ((bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3]) >>> 0;
  const ifd0 = tiff + u32(tiff + 4);
  let gps: JpegGps | null = null;
  const gpsOff = findTagOffset(bytes, ifd0, tiff, u16, u32, 0x8825);
  if (gpsOff != null) {
    const gpsIfd = tiff + gpsOff;
    const latRef = readAscii(bytes, gpsIfd, tiff, u16, u32, 0x0001) || "N";
    const lngRef = readAscii(bytes, gpsIfd, tiff, u16, u32, 0x0003) || "E";
    const lat = readGpsRational(bytes, gpsIfd, tiff, u16, u32, 0x0002);
    const lng = readGpsRational(bytes, gpsIfd, tiff, u16, u32, 0x0004);
    if (lat != null && lng != null) {
      gps = {
        lat: latRef.toUpperCase().startsWith("S") ? -lat : lat,
        lng: lngRef.toUpperCase().startsWith("W") ? -lng : lng,
      };
    }
  }
  let capturedAt: Date | null = null;
  const exifOff = findTagOffset(bytes, ifd0, tiff, u16, u32, 0x8769);
  if (exifOff != null) {
    const exifIfd = tiff + exifOff;
    const original = readAscii(bytes, exifIfd, tiff, u16, u32, 0x9003);
    capturedAt = parseExifDate(original);
  }
  if (!capturedAt) {
    capturedAt = parseExifDate(readAscii(bytes, ifd0, tiff, u16, u32, 0x0132));
  }
  return { gps, capturedAt };
};

const parseExifDate = (value: string | null): Date | null => {
  if (!value) {
    return null;
  }
  const match = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(value.trim());
  if (!match) {
    return null;
  }
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
    Number(match[6])
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

const findTagOffset = (
  bytes: Uint8Array,
  ifd: number,
  tiff: number,
  u16: (n: number) => number,
  u32: (n: number) => number,
  tag: number
): number | null => {
  if (ifd + 2 > bytes.length) {
    return null;
  }
  const count = u16(ifd);
  for (let i = 0; i < count; i++) {
    const entry = ifd + 2 + i * 12;
    if (entry + 12 > bytes.length) {
      return null;
    }
    if (u16(entry) === tag) {
      return u32(entry + 8);
    }
  }
  return null;
};

const readAscii = (
  bytes: Uint8Array,
  ifd: number,
  tiff: number,
  u16: (n: number) => number,
  u32: (n: number) => number,
  tag: number
): string | null => {
  const count = u16(ifd);
  for (let i = 0; i < count; i++) {
    const entry = ifd + 2 + i * 12;
    if (u16(entry) !== tag) {
      continue;
    }
    const len = u32(entry + 4);
    const value = u32(entry + 8);
    if (len <= 4) {
      return String.fromCharCode(bytes[entry + 8] || 32).trim();
    }
    const at = tiff + value;
    let out = "";
    for (let j = 0; j < len && at + j < bytes.length; j++) {
      const c = bytes[at + j];
      if (c === 0) {
        break;
      }
      out += String.fromCharCode(c);
    }
    return out.trim();
  }
  return null;
};

const readGpsRational = (
  bytes: Uint8Array,
  ifd: number,
  tiff: number,
  u16: (n: number) => number,
  u32: (n: number) => number,
  tag: number
): number | null => {
  const count = u16(ifd);
  for (let i = 0; i < count; i++) {
    const entry = ifd + 2 + i * 12;
    if (u16(entry) !== tag) {
      continue;
    }
    const at = tiff + u32(entry + 8);
    if (at + 24 > bytes.length) {
      return null;
    }
    const deg = rat(bytes, at, u32);
    const min = rat(bytes, at + 8, u32);
    const sec = rat(bytes, at + 16, u32);
    if (deg == null || min == null || sec == null) {
      return null;
    }
    return deg + min / 60 + sec / 3600;
  }
  return null;
};

const rat = (bytes: Uint8Array, at: number, u32: (n: number) => number): number | null => {
  const den = u32(at + 4);
  if (den === 0) {
    return null;
  }
  return u32(at) / den;
};
